import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DollarSign, Link2, MousePointer, TrendingUp, Copy, Check, 
  Share2, Instagram, Facebook, Twitter, MessageCircle, Youtube,
  Bell, Gift, ChevronRight, ExternalLink, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AffiliateProgram = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [linkData, setLinkData] = useState(null);
  const [products, setProducts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [dashboardRes, linkRes, productsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/mm-affiliate/dashboard`, config),
        axios.get(`${API}/mm-affiliate/my-link`, config),
        axios.get(`${API}/mm-affiliate/products`),
        axios.get(`${API}/mm-affiliate/notifications`, config)
      ]);

      setDashboard(dashboardRes.data);
      setLinkData(linkRes.data);
      setProducts(productsRes.data.products);
      setNotifications(notificationsRes.data.notifications);
    } catch (error) {
      console.error('Error fetching affiliate data:', error);
      toast.error('Erro ao carregar dados do programa de afiliados');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setCopied(false), 3000);
  };

  const shareToSocial = (platform) => {
    const link = linkData?.short_link || linkData?.full_link;
    const text = encodeURIComponent('🚀 Conheça o MonetizeMaster - A ferramenta definitiva para afiliados digitais! Automatize suas vendas e ganhe dinheiro no piloto automático!');
    
    const urls = {
      whatsapp: `https://wa.me/?text=${text}%20${encodeURIComponent(link)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(link)}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(link)}&title=MonetizeMaster`,
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            💰 Programa de Afiliados MonetizeMaster
          </h1>
          <p className="text-purple-200">
            Ganhe <span className="text-yellow-400 font-bold">50% de comissão</span> em cada venda!
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <MousePointer className="h-8 w-8 text-blue-400" />
              <span className="text-2xl font-bold text-white">{dashboard?.metrics?.total_clicks || 0}</span>
            </div>
            <p className="text-purple-200 text-sm mt-2">Cliques</p>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <TrendingUp className="h-8 w-8 text-green-400" />
              <span className="text-2xl font-bold text-white">{dashboard?.metrics?.total_sales || 0}</span>
            </div>
            <p className="text-purple-200 text-sm mt-2">Vendas</p>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <DollarSign className="h-8 w-8 text-yellow-400" />
              <span className="text-2xl font-bold text-white">
                R$ {dashboard?.commissions?.approved?.toFixed(2) || '0.00'}
              </span>
            </div>
            <p className="text-purple-200 text-sm mt-2">Comissões Aprovadas</p>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <Gift className="h-8 w-8 text-orange-400" />
              <span className="text-2xl font-bold text-white">
                {dashboard?.metrics?.conversion_rate || 0}%
              </span>
            </div>
            <p className="text-purple-200 text-sm mt-2">Taxa de Conversão</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Affiliate Link Section */}
          <div className="md:col-span-2 space-y-6">
            {/* Link Generator */}
            <div className="bg-white rounded-xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Link2 className="h-6 w-6 mr-2 text-purple-600" />
                Seu Link de Afiliado
              </h2>

              <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 mb-2">Seu ID de Afiliado:</p>
                <p className="text-2xl font-bold text-purple-700">{linkData?.affiliate_id}</p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-600">Link Completo:</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={linkData?.full_link || ''}
                      readOnly
                      className="flex-1 bg-gray-100 border rounded-lg px-4 py-2 text-sm"
                    />
                    <button
                      onClick={() => copyLink(linkData?.full_link)}
                      className={`px-4 py-2 rounded-lg transition-all ${
                        copied ? 'bg-green-500 text-white' : 'bg-purple-600 text-white hover:bg-purple-700'
                      }`}
                    >
                      {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {linkData?.short_link && linkData.short_link !== linkData.full_link && (
                  <div>
                    <label className="text-sm text-gray-600">Link Encurtado (Bitly):</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={linkData?.short_link}
                        readOnly
                        className="flex-1 bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm text-green-700 font-medium"
                      />
                      <button
                        onClick={() => copyLink(linkData?.short_link)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                      >
                        <Copy className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Share Buttons */}
              <div className="mt-6">
                <p className="text-sm text-gray-600 mb-3">Compartilhar nas Redes Sociais:</p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => shareToSocial('whatsapp')}
                    className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-all"
                  >
                    <MessageCircle className="h-5 w-5" />
                    WhatsApp
                  </button>
                  <button
                    onClick={() => shareToSocial('facebook')}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all"
                  >
                    <Facebook className="h-5 w-5" />
                    Facebook
                  </button>
                  <button
                    onClick={() => shareToSocial('twitter')}
                    className="flex items-center gap-2 bg-sky-500 text-white px-4 py-2 rounded-lg hover:bg-sky-600 transition-all"
                  >
                    <Twitter className="h-5 w-5" />
                    Twitter
                  </button>
                  <button
                    onClick={() => shareToSocial('linkedin')}
                    className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-all"
                  >
                    <Share2 className="h-5 w-5" />
                    LinkedIn
                  </button>
                </div>
              </div>
            </div>

            {/* Products to Promote */}
            <div className="bg-white rounded-xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                🎁 Produtos para Promover (50% Comissão)
              </h2>
              
              <div className="grid gap-4">
                {products.map((product) => (
                  <div key={product.id} className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                    <img 
                      src={product.banner} 
                      alt={product.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{product.name}</h3>
                      <p className="text-sm text-gray-600">{product.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">R$ {product.price.toFixed(2)}</p>
                      <p className="text-sm text-green-600 font-semibold">
                        Comissão: R$ {product.commission_value.toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Clicks by Source */}
            {dashboard?.clicks_by_source && Object.keys(dashboard.clicks_by_source).length > 0 && (
              <div className="bg-white rounded-xl shadow-xl p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  📊 Cliques por Rede Social
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(dashboard.clicks_by_source).map(([source, count]) => (
                    <div key={source} className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-purple-600">{count}</p>
                      <p className="text-sm text-gray-600 capitalize">{source}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Commission Summary */}
            <div className="bg-white rounded-xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">💵 Suas Comissões</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-600">Pendente</span>
                  <span className="font-bold text-yellow-600">
                    R$ {dashboard?.commissions?.pending?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-600">Aprovado</span>
                  <span className="font-bold text-green-600">
                    R$ {dashboard?.commissions?.approved?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-600">Pago</span>
                  <span className="font-bold text-blue-600">
                    R$ {dashboard?.commissions?.paid?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 bg-purple-50 rounded-lg px-3">
                  <span className="font-bold text-purple-900">Total</span>
                  <span className="font-bold text-purple-600 text-xl">
                    R$ {dashboard?.commissions?.total?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-white rounded-xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Bell className="h-5 w-5 mr-2 text-purple-600" />
                Notificações
              </h2>
              
              {notifications.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {notifications.slice(0, 5).map((notif) => (
                    <div key={notif.id} className={`p-3 rounded-lg ${notif.read ? 'bg-gray-50' : 'bg-yellow-50 border border-yellow-200'}`}>
                      <p className="font-semibold text-sm">{notif.title}</p>
                      <p className="text-xs text-gray-600">{notif.message}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">
                  Nenhuma notificação ainda
                </p>
              )}
            </div>

            {/* Recent Sales */}
            <div className="bg-white rounded-xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">📈 Vendas Recentes</h2>
              
              {dashboard?.recent_sales?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.recent_sales.slice(0, 5).map((sale, i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b last:border-0">
                      <div>
                        <p className="text-sm font-medium">{sale.product}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(sale.date).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">R$ {sale.commission?.toFixed(2)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          sale.status === 'approved' ? 'bg-green-100 text-green-700' :
                          sale.status === 'paid' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {sale.status === 'approved' ? 'Aprovado' : 
                           sale.status === 'paid' ? 'Pago' : 'Pendente'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">
                  Nenhuma venda ainda. Compartilhe seu link!
                </p>
              )}
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchAllData}
              className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-all flex items-center justify-center gap-2"
            >
              <RefreshCw className="h-5 w-5" />
              Atualizar Dados
            </button>
          </div>
        </div>

        {/* How it Works */}
        <div className="mt-8 bg-white rounded-xl shadow-xl p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">🎯 Como Funciona</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="font-bold mb-1">Copie seu Link</h3>
              <p className="text-sm text-gray-600">Use o link personalizado acima</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="font-bold mb-1">Compartilhe</h3>
              <p className="text-sm text-gray-600">Divulgue nas redes sociais</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="font-bold mb-1">Alguém Compra</h3>
              <p className="text-sm text-gray-600">Através do seu link</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl font-bold text-green-600">4</span>
              </div>
              <h3 className="font-bold mb-1">Ganhe 50%</h3>
              <p className="text-sm text-gray-600">Comissão na sua conta!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateProgram;
